<template>
  <div class="predictive" key="predictive">
    <div class="cover" v-if="opened" @click="close"></div>
    <input
      @click="open"
      @input="change"
      key="input"
      ref="input"
      type="text"
      :placeholder="placeholder"
      :size="1 + (value.length === 0 ? placeholder.length : value.length)"
    />
    <div
      :class="{
        results: true,
        hidden: !opened
      }"
    >
      <div class="scroll">
        <div
          v-for="m in matched"
          :class="{
            suggestion: true,
            category: m.type === 'category',
            year: category === 'year',
            verylarge: valueCounts && valueCounts[m.text] > 1000,
            large: !valueCounts || (valueCounts && valueCounts[m.text] > 200),
            medium: valueCounts && valueCounts[m.text] > 50,
            small: valueCounts && valueCounts[m.text] > 10,
            verysmall: valueCounts
          }"
          :key="m.text"
          @click="addTag(m.text)"
        >
          <span>{{ m.parts[0] }}</span>
          <span class="value">{{ m.parts[1] }}</span>
          <span>{{ m.parts[2] }}</span>
        </div>
      </div>
      <div class="remove" v-if="index >= 0" @click="removeTag">
        <div>— remove</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PredictiveTyper",
  props: {
    placeholder: String,
    index: Number,
    fulllist: Array,
    category: String,
    categorize: Boolean,
    showall: Boolean,
    valueCounts: null,
    invert: Boolean
  },
  data() {
    return {
      value: "",
      opened: false
    };
  },
  methods: {
    change(e) {
      this.value = e.target.value;
    },
    addTag(tag) {
      this.opened = false;
      this.$refs.input.value = "";
      this.value = "";

      if (this.index >= 0) {
        this.$store.dispatch("replaceFilter", {
          category: this.category,
          value: tag,
          index: this.index,
          invert: this.invert
        });
      } else {
        this.$store.dispatch("addFilter", {
          category: this.category,
          value: tag,
          invert: this.invert
        });
      }

      this.$emit("changed");
    },
    close() {
      this.opened = false;
    },
    open() {
      this.opened = true;
    },
    removeTag() {
      this.opened = false;
      this.$store.dispatch("removeFilter", this.index);
    },

    sortedTags(values) {
      let sorted = values.slice();
      sorted.sort();

      let categorized = [];
      let alpha = "";

      let categorize = this.categorize;
      if (values.length < 10) categorize = false;

      for (let i = 0; i < sorted.length; i++) {
        if (sorted[i][0].toUpperCase() !== alpha) {
          alpha = sorted[i][0].toUpperCase();
          if (categorize) categorized.push({ type: "category", value: alpha });
        }
        categorized.push({ type: "tag", value: sorted[i] });
      }

      return categorized;
    },

    highlightedTags(list) {
      return list.map(m => ({
        text: m.value,
        type: m.type,
        parts:
          m.type === "category"
            ? [m.value, "", ""]
            : [
                m.value.slice(
                  0,
                  m.value.toLowerCase().indexOf(this.value.toLowerCase())
                ),
                this.value,
                m.value.slice(
                  m.value.toLowerCase().indexOf(this.value.toLowerCase()) +
                    this.value.length
                )
              ]
      }));
    }
  },
  computed: {
    matched() {
      if (this.value.length < 1) {
        if (this.showall) {
          return this.highlightedTags(this.sortedTags(this.fulllist));
        } else {
          return [];
        }
      }

      let matched = this.fulllist.filter(ocr => {
        return ocr.toLowerCase().indexOf(this.value.toLowerCase()) >= 0;
      });

      if (!this.showall) {
        matched = matched
          .slice(0, 10)
          .map(m => ({ type: this.category, value: m }));
      } else {
        matched = this.sortedTags(matched);
      }

      return this.highlightedTags(matched);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/constants/_variables.scss";

.predictive {
  border-bottom: 1px solid black;
  position: relative;
  font-size: 16px;
  margin-top: 9px;

  input[type="text"] {
    min-width: 150px;
    width: auto;
    font-size: 16px;
    font: inherit;
    font-weight: 400;
    border: 0;
    padding: 0em 0.25em;
    position: relative;
    z-index: 9;
    outline: none;

    &::placeholder {
      color: black;
    }

    &:focus {
      background-color: $light-gold;
    }

    &:hover {
      background-color: $light-gold;
    }

    @media (max-width: 815px) {
      min-width: 100px;
    }
  }

  @media (hover: hover) {
    &:hover {
      .remove {
        display: block;
      }
    }
  }
}

.cover {
  position: fixed;
  top: 0px;
  left: 0px;
  height: 100vh;
  width: 100vw;
  z-index: 10;
}

.results {
  border-bottom: 1px solid black;
  background-color: $white;
  position: absolute;
  font-size: 14px;
  max-height: 313px;
  z-index: 12000;

  @media (max-width: 815px) {
    min-width: 100px;
  }

  @media (max-width: 815px) and (orientation: landscape) {
    max-height: 170px;
  }

  .scroll {
    max-height: 313px;
    overflow: auto;

    @media (max-width: 815px) and (orientation: landscape) {
      max-height: 170px;
    }
  }

  &.hidden {
    border: none;

    .scroll {
      /* Specifically hide scroll area when hidden */
      display: none;
    }
  }

  &:not(.hidden) {
    .remove {
      /* Don't rely on hover to show remove button */
      display: block;
    }
  }

  .suggestion {
    cursor: pointer;
    text-align: left;
    padding: 0.25em 0em;
    padding-right: 10px;
    padding-left: 13px;

    &.verysmall {
      // color: $brown-grey;
      border-left: 1px solid $light-gold;
      padding-left: 12px;
    }

    &.small {
      border-left: 3px solid $light-gold;
      padding-left: 10px;

      // color: #555;
    }

    &.medium {
      // color: $med-gray;
      border-left: 5px solid $light-gold;
      padding-left: 8px;
    }

    &.large {
      // color: black;
      border-left: 7px solid $light-gold;
      padding-left: 6px;
    }

    &.verylarge {
      // font-weight: 500;
      border-left: 10px solid $light-gold;
      padding-left: 3px;
    }

    &.year {
      border-left: 0px;
      padding-left: 10px;
    }

    &:hover {
      border-left-color: $copper;
    }

    &.category {
      font-weight: 600;
      color: black;
      cursor: default;
      border-left-width: 0px;

      &:hover {
        background-color: inherit;
      }
    }

    &:hover {
      background-color: $light-gold;
    }

    span.value {
      font-weight: 500;
    }
  }
}

.remove {
  display: none;
  position: absolute;
  bottom: -31px;
  background-color: $copper;
  color: $white;
  width: 100%;
  min-width: 150px;
  padding: 0.5em 0.5em;
  text-align: left;
  font-size: 14px;
  z-index: 1000;
  cursor: pointer;
  font-weight: 500;

  @media (max-width: 815px) {
    min-width: 100px;
  }

  &:hover {
    color: $light-gold;
  }
}
</style>
